import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import * as Notebooks from 'modules/notebooks';
import { NotebookFormModal } from './NotebookFormModal';

/** Which dialog the Notebooks grid (desktop or phone) has open. `title` is the notebook's on-screen name. */
export type NotebookDialog =
  | { kind: 'create' }
  | { kind: 'rename'; notebook: Notebooks.Types.Notebook; title: string }
  | { kind: 'delete'; notebook: Notebooks.Types.Notebook; title: string };

interface Props {
  dialog: NotebookDialog | null;
  onClose: () => void;
}

/** The create/rename form and the delete confirmation, shared by the desktop grid and the phone list. */
export const NotebookDialogs: React.FC<Props> = ({ dialog, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteNotebook = Notebooks.Hooks.useDelete();

  const handleDelete = async () => {
    if (dialog?.kind !== 'delete') return;
    try {
      await deleteNotebook.mutateAsync(dialog.notebook._id);
      onClose();
    } catch {
      // The hook already toasted the error; the dialog stays open to retry.
    }
  };

  return (
    <>
      <NotebookFormModal
        open={dialog?.kind === 'create' || dialog?.kind === 'rename'}
        onClose={onClose}
        notebook={dialog?.kind === 'rename' ? dialog.notebook : null}
        displayTitle={dialog?.kind === 'rename' ? dialog.title : undefined}
        onSuccess={(saved) => {
          onClose();
          // A new notebook is empty: take the user straight in to add its first source.
          if (dialog?.kind === 'create') navigate(`/app/documents/${saved._id}`);
        }}
      />

      <ConfirmDialog
        open={dialog?.kind === 'delete'}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={t('notebooks.delete_title')}
        description={dialog?.kind === 'delete' ? t('notebooks.delete_desc', { title: dialog.title }) : undefined}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={deleteNotebook.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default NotebookDialogs;
